import { useCallback, useRef } from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { IconsMap } from '@nx-frontend/shared/ui/icon';
import { useClickOutside } from '@nx-frontend/shared/hooks';
import { NotificationsPortalContext, User } from '@nx-frontend/shared/types';
import { NotificationPanelFilterButton } from './notification-panel-filter-button';
import { NotificationPanelFilterByPlace } from './notification-panel-filter-by-place';
import { NotificationPanelFilterByUser } from './notification-panel-filter-by-user';
import { NotificationPanelFilterSelectedMembers } from './notification-panel-filter-selected-members';
import { NotificationPanelFilterSelectedPlace } from './notification-panel-filter-selected-place';
import { NotificationPanelFilterSelector } from './notification-panel-filter-selector';
import { NotificationPanelFilterWorkspace } from './notification-panel-filter-workspace-icon';
import {
  $notificationPanelFilter,
  setNotificationPanelFilterEvent,
  setNotificationPanelFilterValuesEvent,
  setShowNotificationPanelFilterByPlaceEvent,
  setShowNotificationPanelFilterByUserEvent,
} from './store';

const transitionClasses: Record<string, string> = {
  entering: 'opacity-1 transform translate-y-0',
  entered: 'opacity-1 transform translate-y-0',
  exiting: 'opacity-0 transform translate-y-0',
  exited: 'opacity-0 transform translate-y-[-20px]',
};

interface NotificationPanelFilterProps {
  portalContext: NotificationsPortalContext;
  members: User[];
  workspace?: NotificationPanelFilterWorkspace;
}

export function NotificationPanelFilter(props: NotificationPanelFilterProps) {
  const { portalContext, members, workspace } = props;

  const [notificationPanelFilter] = useUnit([$notificationPanelFilter]);

  const [
    setShowNotificationPanelFilterByPlace,
    setShowNotificationPanelFilterByUser,
    setNotificationPanelFilter,
    setNotificationPanelFilterValues,
  ] = useUnit([
    setShowNotificationPanelFilterByPlaceEvent,
    setShowNotificationPanelFilterByUserEvent,
    setNotificationPanelFilterEvent,
    setNotificationPanelFilterValuesEvent,
  ]);

  const nodeRef = useRef(null);

  const handleHideOptions = useCallback(() => {
    setShowNotificationPanelFilterByPlace(false);
    setShowNotificationPanelFilterByUser(false);
  }, [setShowNotificationPanelFilterByPlace, setShowNotificationPanelFilterByUser]);

  const handleClearAll = useCallback(() => {
    setNotificationPanelFilterValues({
      workspaceId: null,
      portalId: null,
      userIds: [],
    });
  }, [setNotificationPanelFilterValues]);

  const t = useTranslations('notification_panel');

  useClickOutside({
    enabled: true,
    elementRef: nodeRef,
    onClickOutside: (e: MouseEvent) => {
      const filterBtn = document.querySelector('.notification-panel__filter-btn');
      if (filterBtn && filterBtn.contains(e.target as Node)) {
        return;
      }
      setNotificationPanelFilter({
        showFilterByPlace: false,
        showFilterByUser: false,
        show: false,
      });
    },
  });

  return (
    <Transition in={notificationPanelFilter.show} nodeRef={nodeRef} timeout={600} unmountOnExit={true}>
      {(state: TransitionStatus) => (
        <div
          ref={nodeRef}
          data-state={state}
          className={classNames(
            'w-[320px] border border-neutral-200 rounded bg-white absolute p-3 pb-4 shadow-[0_1.49px_11.21px_0_#A3A8AD38] transition-all',
            transitionClasses[state]
          )}
          onClick={handleHideOptions}
        >
          <NotificationPanelFilterSelector
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setShowNotificationPanelFilterByPlace(!notificationPanelFilter.showFilterByPlace);
            }}
            icon={IconsMap.FOLDER}
            title={t('notification_panel-sort_by_place')}
          >
            {notificationPanelFilter.portalId || notificationPanelFilter.workspaceId ? (
              <NotificationPanelFilterSelectedPlace portalContext={portalContext} workspace={workspace} />
            ) : null}
          </NotificationPanelFilterSelector>
          <NotificationPanelFilterSelector
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setShowNotificationPanelFilterByUser(!notificationPanelFilter.showFilterByUser);
            }}
            icon={IconsMap.PERSON}
            title={t('notification_panel-sort_by_user')}
          >
            {notificationPanelFilter.userIds.length ? (
              <NotificationPanelFilterSelectedMembers members={members} />
            ) : null}
          </NotificationPanelFilterSelector>

          <div className="flex pt-1.5 pr-3">
            <NotificationPanelFilterButton
              onClick={handleClearAll}
              title={t('notification_panel-clear_all')}
              additionClasses="ml-auto"
            />
          </div>
          {/*TOTO: if no access to portal not show filter by place*/}
          <NotificationPanelFilterByPlace portalContext={portalContext} workspace={workspace} />
          <NotificationPanelFilterByUser members={members} />
        </div>
      )}
    </Transition>
  );
}
